import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

declare const fbClient: any;

@Injectable({
    providedIn: 'root'
})
export class FeatbitService implements CanActivate {
    private initialized = false;
    private client: any;

    constructor(private http: HttpClient, private router: Router) { }

    /**
     * Initialize FeatBit client with configuration
     */
    init(config: {
        secret: string; // The environment secret from FeatBit
        api?: string; // Deprecated, use streamingUri and eventsUri instead
        streamingUri?: string;
        eventsUri?: string;
        user: {
            keyId: string;
            name: string;
            customizedProperties?: any[];
        };
    }): void {
        if (this.initialized) {
            console.warn('FeatBit client already initialized');
            return;
        }

        if (typeof fbClient === 'undefined') {
            console.error('FeatBit SDK not loaded. Make sure the UMD bundle is included in index.html');
            return;
        }

        this.client = fbClient;
        this.client.init(config);
        this.initialized = true;
        console.log('FeatBit client initialized');
    }

    /**
     * Get a boolean feature flag variation
     */
    boolVariation(flagKey: string, defaultValue: boolean = false): boolean {
        if (!this.initialized || !this.client) {
            console.warn('FeatBit client not initialized');
            return defaultValue;
        }
        return this.client.variation(flagKey, defaultValue);
    }

    /**
     * Get a string feature flag variation
     */
    stringVariation(flagKey: string, defaultValue: string = ''): string {
        if (!this.initialized || !this.client) {
            console.warn('FeatBit client not initialized');
            return defaultValue;
        }
        return this.client.variation(flagKey, defaultValue);
    }

    /**
     * Get a numeric feature flag variation
     */
    numberVariation(flagKey: string, defaultValue: number = 0): number {
        if (!this.initialized || !this.client) {
            console.warn('FeatBit client not initialized');
            return defaultValue;
        }
        return this.client.variation(flagKey, defaultValue);
    }

    /**
     * Get a JSON feature flag variation
     */
    jsonVariation(flagKey: string, defaultValue: any = {}): any {
        if (!this.initialized || !this.client) {
            console.warn('FeatBit client not initialized');
            return defaultValue;
        }
        return this.client.variation(flagKey, defaultValue);
    }

    /**
     * Listen to feature flag updates
     */
    on(event: string, callback: (changes: any) => void): void {
        if (!this.initialized || !this.client) {
            console.warn('FeatBit client not initialized');
            return;
        }
        this.client.on(event, callback);
    }

    /**
     * Remove event listener
     */
    off(event: string, callback?: (changes: any) => void): void {
        if (!this.initialized || !this.client) {
            console.warn('FeatBit client not initialized');
            return;
        }
        this.client.off(event, callback);
    }

    /**
     * Check if client is initialized
     */
    isInitialized(): boolean {
        return this.initialized;
    }

    async canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean | UrlTree> {
        const flagKey = route.data['flagKey'] as string;
        if (!flagKey) {
            return true;
        }

        const enabled = await this.isEnabled(flagKey);

        if (enabled) {
            return true;
        }

        return this.router.parseUrl('/marktplatz');
    }

    isEnabled(flagKey: string, userId?: string): boolean {
        return this.boolVariation(flagKey, false);
    }
}
